import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { onAuthStateChanged, type User } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import * as Sentry from '@sentry/react'
import { auth, db } from '@/lib/firebase'
import * as authService from '@/services/authService'
import { createLogger } from '@/utils/logger'
import type { UserRole } from '@/types/models'
import type { JoinData, RegisterData } from '@/types/api'

const log = createLogger('Auth')

interface AuthContextValue {
  user: User | null
  name: string | null
  churchId: string | null
  churchName: string | null
  role: UserRole | null
  isDemo: boolean
  loading: boolean
  error: Error | null
  login: (email: string, password: string) => Promise<{ role: UserRole | null }>
  register: (data: RegisterData) => Promise<void>
  joinChurch: (data: JoinData) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function extractUserData(user: User) {
  // Try custom claims first
  const tokenResult = await user.getIdTokenResult()
  const claimsChurchId = (tokenResult.claims.churchId as string) || null
  const claimsRole = (tokenResult.claims.role as UserRole) || null
  const claimsIsDemo = !!tokenResult.claims.isDemo

  log.debug('extractUserData — claims:', {
    churchId: claimsChurchId,
    role: claimsRole,
    isDemo: claimsIsDemo,
    uid: user.uid,
  })

  let churchId = claimsChurchId
  let role = claimsRole
  let isDemo = claimsIsDemo
  let name: string | null = user.displayName || null

  // Always read user doc for name fallback and missing claims
  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid))
    if (userDoc.exists()) {
      const data = userDoc.data()
      churchId = churchId || (data.churchId as string) || null
      role = role || (data.role as UserRole) || null
      isDemo = isDemo || !!data.isDemo
      name = name || (data.name as string) || null
      log.debug('user doc read OK — churchId:', churchId, 'role:', role)
    } else {
      log.warn('user doc does not exist for uid:', user.uid)
    }
  } catch (err) {
    log.error('failed to read user doc:', err)
    throw err
  }

  // Fetch church name if we have a churchId
  let churchName: string | null = null
  if (churchId) {
    log.debug(
      'reading church doc — path: churches/' + churchId,
      '| claimsChurchId:',
      claimsChurchId,
      '| uid:',
      user.uid,
    )
    try {
      const churchDoc = await getDoc(doc(db, 'churches', churchId))
      if (churchDoc.exists()) {
        churchName = (churchDoc.data().name as string) || null
      } else {
        log.warn('church doc does NOT exist at churches/' + churchId)
      }
      log.debug('church doc read OK — name:', churchName)
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string }
      log.error(
        'failed to read church doc — path: churches/' + churchId,
        '| code:',
        e?.code,
        '| message:',
        e?.message,
      )
      // Don't throw — church name is non-critical. The churchId is already set
      // from claims or user doc, so the app can still function without the name.
    }
  } else {
    log.warn('no churchId available, skipping church doc read')
  }

  return { churchId, role, isDemo, churchName, name }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [name, setName] = useState<string | null>(null)
  const [churchId, setChurchId] = useState<string | null>(null)
  const [churchName, setChurchName] = useState<string | null>(null)
  const [role, setRole] = useState<UserRole | null>(null)
  const [isDemo, setIsDemo] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const refreshUser = async () => {
    if (!auth.currentUser) return
    await auth.currentUser.getIdToken(true)
    const claims = await extractUserData(auth.currentUser)
    setName(claims.name)
    setChurchId(claims.churchId)
    setChurchName(claims.churchName)
    setRole(claims.role)
    setIsDemo(claims.isDemo)
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser)
      if (firebaseUser) {
        try {
          const claims = await extractUserData(firebaseUser)
          log.debug(
            'role:',
            claims.role,
            'churchId:',
            claims.churchId,
            'isDemo:',
            claims.isDemo,
          )
          setName(claims.name)
          setChurchId(claims.churchId)
          setChurchName(claims.churchName)
          setRole(claims.role)
          setIsDemo(claims.isDemo)
          Sentry.setUser({
            id: firebaseUser.uid,
            email: firebaseUser.email ?? undefined,
          })
        } catch {
          setName(null)
          setChurchId(null)
          setChurchName(null)
          setRole(null)
          setIsDemo(false)
        }
      } else {
        setName(null)
        setChurchId(null)
        setChurchName(null)
        setRole(null)
        setIsDemo(false)
        Sentry.setUser(null)
      }
      setLoading(false)
    })
    return unsubscribe
  }, [])

  const login = async (
    email: string,
    password: string,
  ): Promise<{ role: UserRole | null }> => {
    setError(null)
    try {
      await authService.login(email, password)
      // Use the same extraction logic as onAuthStateChanged so we catch
      // roles stored in custom claims OR in the Firestore user doc.
      const currentUser = auth.currentUser
      if (currentUser) {
        const data = await extractUserData(currentUser)
        return { role: data.role }
      }
      return { role: null }
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Login failed')
      setError(e)
      throw e
    }
  }

  const register = async (data: RegisterData) => {
    setError(null)
    try {
      log.debug('register — starting...')
      await authService.register(data)
      log.debug(
        'register — authService.register complete, waiting 2s for claims...',
      )
      // Wait for Cloud Function to set custom claims
      await new Promise((resolve) => setTimeout(resolve, 2000))
      log.debug('register — calling refreshUser...')
      await refreshUser()
      log.debug('register — refreshUser complete')
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Registration failed')
      setError(e)
      throw e
    }
  }

  const joinChurchFn = async (data: JoinData) => {
    setError(null)
    try {
      await authService.joinChurch(
        data.email,
        data.password,
        data.name,
        data.churchId,
      )
      // Wait for Cloud Function to set custom claims
      await new Promise((resolve) => setTimeout(resolve, 2000))
      await refreshUser()
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to join church')
      setError(e)
      throw e
    }
  }

  const logoutFn = async () => {
    setError(null)
    try {
      await authService.logout()
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Logout failed')
      setError(e)
      throw e
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        name,
        churchId,
        churchName,
        role,
        isDemo,
        loading,
        error,
        login,
        register,
        joinChurch: joinChurchFn,
        logout: logoutFn,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
